import { facets, filterAssignments } from "../facets.js";
import { convertFacet } from "../units-conversion.js";
import { columnState } from "./column-state.js";

const FIXED_FIRST = "item-number";
const FIXED_LAST = "price";

/**
 * Get facet keys that should be excluded because they're used to build composite facets
 * For example, individual type attributes (tubing-type, balloons-type, etc.) are used to build the "type" column
 * @returns {Set<string>} Set of facet keys to exclude
 */
function getCompositeFacetExclusions() {
  const exclusions = new Set();

  // Find all facets with buildFromAttributes
  Object.keys(facets).forEach((key) => {
    const facet = facets[key];
    if (facet.buildFromAttributes && Array.isArray(facet.buildFromAttributes)) {
      // For each attribute in buildFromAttributes, find the facet key that uses it
      facet.buildFromAttributes.forEach((buildAttribute) => {
        // Find facet key that uses this attribute
        Object.keys(facets).forEach((facetKey) => {
          if (facetKey === key) return; // Don't exclude the composite facet itself

          const candidateFacet = facets[facetKey];
          if (!candidateFacet || !candidateFacet.attribute) return;

          const candidateConfig = convertFacet(candidateFacet);
          if (candidateConfig.attribute === buildAttribute) {
            exclusions.add(facetKey);
          }
        });
      });
    }
  });

  return exclusions;
}

/**
 * Get all available column keys from facets.js, excluding composite facet exclusions
 * @returns {Array<string>} Array of column keys
 */
function getAllAvailableColumnKeys() {
  const exclusions = getCompositeFacetExclusions();
  return Object.keys(facets).filter((key) => !exclusions.has(key));
}

/**
 * Create column config object from key
 * @param {string} key - Column key
 * @returns {Object|null} Column config object
 */
function createColumnConfig(key) {
  if (!facets[key]) return null;

  const facet = convertFacet(facets[key]);
  return {
    ...facet,
    key: key,
    label: facet.label_column ?? facet.label,
  };
}

/**
 * Column Configuration Popup Component
 */
export class ColumnConfigPopup {
  constructor() {
    this.popup = null;
    this.currentCategory = null;
    this.selectedColumns = [];
    this.defaultColumns = [];
    this.onSaveCallback = null;
    this.savedScrollPosition = 0;
    this.searchFilter = "";
  }

  /**
   * Get default columns for a category from displayHits
   * @param {string} category - Category slug
   * @returns {Array<string>} Array of default column keys
   */
  getDefaultColumnsForCategory(category) {
    const categorySetup = filterAssignments[category] ?? filterAssignments["DEFAULT"];

    let columns = [];

    // For SERP, use only SERP.displayHits (as in search.js)
    if (category === "SERP") {
      columns = [...(categorySetup.displayHits || [])];
    } else {
      // For other categories, merge category displayHits with EVERY.displayHits (as in product-category.js)
      const everyColumns = filterAssignments["EVERY"]?.displayHits || [];
      columns = [
        ...(categorySetup.displayHits || []),
        ...everyColumns,
      ];
    }

    // Sort by orderColumns
    columns.sort((a, b) => a.orderColumns - b.orderColumns);

    // Ensure fixed columns are in place
    const columnKeys = columns.map((c) => c.key);
    return this.ensureFixedColumns(columnKeys);
  }

  /**
   * Ensure fixed columns are in correct positions
   * @param {Array<string>} columnKeys - Array of column keys
   * @returns {Array<string>} Array with fixed columns in correct positions
   */
  ensureFixedColumns(columnKeys) {
    const result = [];
    const middleColumns = [];

    // Separate fixed and middle columns
    columnKeys.forEach((key) => {
      if (key === FIXED_FIRST) {
        // Will add at start
      } else if (key === FIXED_LAST) {
        // Will add at end
      } else {
        middleColumns.push(key);
      }
    });

    // Build result: item-number first, middle columns, price last
    result.push(FIXED_FIRST);
    result.push(...middleColumns);
    result.push(FIXED_LAST);

    return result;
  }

  /**
   * Open the popup
   * @param {string} category - Current category slug
   * @param {Array} currentColumns - Currently displayed columns
   * @param {Function} onSave - Callback when user saves
   */
  open(category, currentColumns, onSave) {
    this.currentCategory = category;
    this.onSaveCallback = onSave;
    this.searchFilter = ""; // Reset search filter when opening

    // Get default columns for this category
    this.defaultColumns = this.getDefaultColumnsForCategory(category);

    // Extract column keys from current columns
    const currentKeys = currentColumns.map((col) => col.key);

    // Initialize selected columns: item-number first, then current columns (excluding fixed ones), then price last
    this.selectedColumns = [
      FIXED_FIRST,
      ...currentKeys.filter((key) => key !== FIXED_FIRST && key !== FIXED_LAST),
      FIXED_LAST,
    ];

    this.render();
  }

  /**
   * Close the popup
   */
  close() {
    if (this.popup) {
      this.popup.remove();
      this.popup = null;
    }
  }

  /**
   * Render the popup
   */
  render() {
    // Save scroll position before closing/re-rendering
    const contentElement = this.popup?.querySelector(".chamfr-column-config-content");
    if (contentElement) {
      this.savedScrollPosition = contentElement.scrollTop;
    }

    // Close existing popup if any
    this.close();

    const availableKeys = getAllAvailableColumnKeys();
    const availableColumns = availableKeys
      .map((key) => createColumnConfig(key))
      .filter((col) => col !== null);

    // Create popup HTML
    const popupHTML = `
      <div class="chamfr-column-config-overlay">
        <div class="chamfr-column-config-popup">
          <div class="chamfr-column-config-header">
            <h3>Configure Columns</h3>
            <button class="chamfr-column-config-close" aria-label="Close">&times;</button>
          </div>
          <div class="chamfr-column-config-content">
            <div class="chamfr-column-config-instructions">
              <p>Use <button class="chamfr-column-config-add-inline" disabled>+</button> to add columns and <button class="chamfr-column-config-remove-inline" disabled>−</button> to remove them. Drag the <span class="chamfr-column-config-drag-handle-inline">⋮⋮</span> handle to reorder selected columns. Item Number and Price are always shown.</p>
            </div>
            <div class="chamfr-column-config-list">
              ${this.renderColumnList(availableColumns)}
            </div>
          </div>
          <div class="chamfr-column-config-footer">
            <button class="chamfr-column-config-reset btn btn-secondary">Reset to Default</button>
            <div class="chamfr-column-config-footer-right">
              <button class="chamfr-column-config-cancel btn btn-outlined">Cancel</button>
              <button class="chamfr-column-config-save btn btn-primary">Save</button>
            </div>
          </div>
        </div>
      </div>
    `;

    // Insert into DOM
    document.body.insertAdjacentHTML("beforeend", popupHTML);
    this.popup = document.querySelector(".chamfr-column-config-overlay");

    // Restore scroll position after rendering
    const newContentElement = this.popup?.querySelector(".chamfr-column-config-content");
    if (newContentElement && this.savedScrollPosition > 0) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        newContentElement.scrollTop = this.savedScrollPosition;
      });
    }

    // Bind events
    this.bindEvents();

    // Initialize drag and drop
    this.initDragAndDrop();
  }

  /**
   * Update only the available columns section (for search filtering)
   */
  updateAvailableColumns() {
    if (!this.popup) return;

    // Save scroll position
    const contentElement = this.popup.querySelector(".chamfr-column-config-content");
    const savedScroll = contentElement ? contentElement.scrollTop : 0;

    const availableKeys = getAllAvailableColumnKeys();
    const availableColumns = availableKeys
      .map((key) => createColumnConfig(key))
      .filter((col) => col !== null);

    const selectedSet = new Set(this.selectedColumns);
    const unselectedCols = availableColumns.filter(
      (col) => !selectedSet.has(col.key)
    );

    // Filter based on search term
    const filteredCols = this.searchFilter
      ? unselectedCols.filter((col) =>
          col.label.toLowerCase().includes(this.searchFilter.toLowerCase()) ||
          col.key.toLowerCase().includes(this.searchFilter.toLowerCase())
        )
      : unselectedCols;

    // Update the available columns list
    const availableList = this.popup.querySelector(".chamfr-column-config-available-list");
    if (availableList) {
      let html = "";

      if (filteredCols.length === 0 && this.searchFilter) {
        html = '<li class="chamfr-column-config-no-results">No columns found matching "' + this.searchFilter + '"</li>';
      } else {
        filteredCols.forEach((col) => {
          html += `
            <li class="chamfr-column-config-item">
              <span class="chamfr-column-config-label">${col.label}</span>
              <button class="chamfr-column-config-add" data-key="${col.key}" aria-label="Add column" title="Add column">+</button>
            </li>
          `;
        });
      }

      availableList.innerHTML = html;

      // Re-bind add button events
      this.popup.querySelectorAll(".chamfr-column-config-add").forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const key = button.dataset.key;
          this.addColumn(key);
        });
      });

      // Restore scroll position
      if (contentElement && savedScroll > 0) {
        requestAnimationFrame(() => {
          contentElement.scrollTop = savedScroll;
        });
      }
    }
  }

  /**
   * Render the column list
   * @param {Array} availableColumns - Available column configs
   * @returns {string} HTML string
   */
  renderColumnList(availableColumns) {
    const selectedSet = new Set(this.selectedColumns);

    // Separate selected and unselected columns
    const selectedCols = this.selectedColumns
      .map((key) => availableColumns.find((col) => col.key === key))
      .filter((col) => col !== undefined);

    const unselectedCols = availableColumns.filter(
      (col) => !selectedSet.has(col.key)
    );

    let html = '<div class="chamfr-column-config-selected">';
    html += "<h4>Selected Columns</h4>";
    html += '<ul class="chamfr-column-config-sortable">';

    selectedCols.forEach((col, index) => {
      const isFixed = col.key === FIXED_FIRST || col.key === FIXED_LAST;

      html += `
        <li
          class="chamfr-column-config-item ${isFixed ? "fixed" : "draggable"}"
          data-key="${col.key}"
          data-index="${index}"
          ${!isFixed ? 'draggable="true"' : ''}
        >
          ${!isFixed ? '<span class="chamfr-column-config-drag-handle" aria-label="Drag to reorder">⋮⋮</span>' : ''}
          <span class="chamfr-column-config-label">${col.label}</span>
          ${isFixed ? '<span class="chamfr-column-config-fixed-badge">Always shown</span>' : `<button class="chamfr-column-config-remove" data-key="${col.key}" aria-label="Remove column" title="Remove column">−</button>`}
        </li>
      `;
    });

    html += "</ul>";
    html += "</div>";

    html += '<div class="chamfr-column-config-available">';
    html += "<h4>Available Columns</h4>";
    html += '<div class="chamfr-column-config-search-wrapper">';
    html += '<input type="text" class="chamfr-column-config-search" placeholder="Search columns..." value="' + (this.searchFilter || '') + '" />';
    html += '</div>';
    html += '<ul class="chamfr-column-config-available-list">';

    // Filter unselected columns based on search term
    const filteredCols = this.searchFilter
      ? unselectedCols.filter((col) =>
          col.label.toLowerCase().includes(this.searchFilter.toLowerCase()) ||
          col.key.toLowerCase().includes(this.searchFilter.toLowerCase())
        )
      : unselectedCols;

    if (filteredCols.length === 0 && this.searchFilter) {
      html += '<li class="chamfr-column-config-no-results">No columns found matching "' + this.searchFilter + '"</li>';
    } else {
      filteredCols.forEach((col) => {
        html += `
          <li class="chamfr-column-config-item">
            <span class="chamfr-column-config-label">${col.label}</span>
            <button class="chamfr-column-config-add" data-key="${col.key}" aria-label="Add column" title="Add column">+</button>
          </li>
        `;
      });
    }

    html += "</ul>";
    html += "</div>";

    return html;
  }

  /**
   * Bind event handlers
   */
  bindEvents() {
    if (!this.popup) return;

    // Close button
    this.popup
      .querySelector(".chamfr-column-config-close")
      .addEventListener("click", () => this.close());

    // Reset button
    this.popup
      .querySelector(".chamfr-column-config-reset")
      .addEventListener("click", () => this.handleReset());

    // Cancel button
    this.popup
      .querySelector(".chamfr-column-config-cancel")
      .addEventListener("click", () => this.close());

    // Save button
    this.popup
      .querySelector(".chamfr-column-config-save")
      .addEventListener("click", () => this.handleSave());

    // Overlay click to close
    this.popup.addEventListener("click", (e) => {
      if (e.target === this.popup) {
        this.close();
      }
    });

    // Add column buttons
    this.popup.querySelectorAll(".chamfr-column-config-add").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const key = button.dataset.key;
        this.addColumn(key);
      });
    });

    // Remove column buttons
    this.popup.querySelectorAll(".chamfr-column-config-remove").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const key = button.dataset.key;
        this.removeColumn(key);
      });
    });

    // Search input
    const searchInput = this.popup.querySelector(".chamfr-column-config-search");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.searchFilter = e.target.value;
        // Re-render only the available columns section to preserve scroll position
        this.updateAvailableColumns();
      });
    }

    // Escape key to close
    document.addEventListener("keydown", this.handleKeyDown = (e) => {
      if (e.key === "Escape" && this.popup) {
        this.close();
      }
    });
  }

  /**
   * Handle keyboard events
   */
  handleKeyDown(e) {
    if (e.key === "Escape" && this.popup) {
      this.close();
    }
  }

  /**
   * Add a column to selected columns
   * @param {string} key - Column key
   */
  addColumn(key) {
    if (this.selectedColumns.includes(key)) return;
    if (key === FIXED_FIRST || key === FIXED_LAST) return;

    // Save scroll position before re-rendering
    const contentElement = this.popup?.querySelector(".chamfr-column-config-content");
    if (contentElement) {
      this.savedScrollPosition = contentElement.scrollTop;
    }

    // Insert before price (last item)
    const insertIndex = this.selectedColumns.length - 1;
    this.selectedColumns.splice(insertIndex, 0, key);
    this.render();
  }

  /**
   * Remove a column from selected columns
   * @param {string} key - Column key
   */
  removeColumn(key) {
    if (key === FIXED_FIRST || key === FIXED_LAST) return;

    // Save scroll position before re-rendering
    const contentElement = this.popup?.querySelector(".chamfr-column-config-content");
    if (contentElement) {
      this.savedScrollPosition = contentElement.scrollTop;
    }

    const index = this.selectedColumns.indexOf(key);
    if (index !== -1) {
      this.selectedColumns.splice(index, 1);
      this.render();
    }
  }

  /**
   * Initialize drag and drop
   */
  initDragAndDrop() {
    if (!this.popup) return;

    const sortableList = this.popup.querySelector(".chamfr-column-config-sortable");
    if (!sortableList) return;

    let draggedElement = null;
    let draggedIndex = null;
    let placeholder = null;

    // Create placeholder element
    const createPlaceholder = () => {
      const placeholderEl = document.createElement("li");
      placeholderEl.className = "chamfr-column-config-item chamfr-column-config-placeholder";
      placeholderEl.innerHTML = '<span class="chamfr-column-config-label">Drop here</span>';
      return placeholderEl;
    };

    // Get all draggable items
    const getDraggableItems = () => {
      return Array.from(sortableList.querySelectorAll(".draggable"));
    };

    // Get index of element in selectedColumns (accounting for fixed first/last)
    const getActualIndex = (element) => {
      const key = element.dataset.key;
      return this.selectedColumns.indexOf(key);
    };

    // Handle drag start
    sortableList.addEventListener("dragstart", (e) => {
      // Prevent drag if clicking on buttons or input fields
      const target = e.target;
      const isButton = target.tagName === "BUTTON" || target.closest("button");
      const isInput = target.tagName === "INPUT" || target.closest("input");

      // Prevent drag if clicking on interactive elements
      if (isButton || isInput) {
        e.preventDefault();
        return;
      }

      if (!e.target.closest(".draggable")) return;

      draggedElement = e.target.closest(".draggable");
      draggedIndex = getActualIndex(draggedElement);

      // Don't allow dragging fixed columns
      const key = draggedElement.dataset.key;
      if (key === FIXED_FIRST || key === FIXED_LAST) {
        e.preventDefault();
        return;
      }

      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", draggedElement.outerHTML);

      draggedElement.classList.add("dragging");

      // Create placeholder
      placeholder = createPlaceholder();
      draggedElement.parentNode.insertBefore(placeholder, draggedElement.nextSibling);
    });

    // Handle drag over
    sortableList.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      if (!draggedElement || !placeholder) return;

      const afterElement = getDragAfterElement(sortableList, e.clientY);

      if (afterElement == null) {
        // Check if we can drop before price (last fixed item)
        const priceItem = sortableList.querySelector('[data-key="' + FIXED_LAST + '"]');
        if (priceItem) {
          // Only insert before price if placeholder isn't already there
          if (priceItem.previousSibling !== placeholder) {
            sortableList.insertBefore(placeholder, priceItem);
          }
        } else {
          // If no price item found, append to end
          sortableList.appendChild(placeholder);
        }
      } else {
        // Don't allow dropping before item-number or after/on price
        const afterKey = afterElement.dataset?.key;

        if (afterKey === FIXED_FIRST) {
          // Can't drop before item-number, drop after it
          const itemNumberItem = sortableList.querySelector('[data-key="' + FIXED_FIRST + '"]');
          if (itemNumberItem && itemNumberItem.nextSibling !== placeholder) {
            sortableList.insertBefore(placeholder, itemNumberItem.nextSibling);
          }
        } else if (afterKey === FIXED_LAST) {
          // Can't drop on or after price, drop before it
          const priceItem = sortableList.querySelector('[data-key="' + FIXED_LAST + '"]');
          if (priceItem && priceItem.previousSibling !== placeholder) {
            sortableList.insertBefore(placeholder, priceItem);
          }
        } else {
          // Regular draggable item - insert before it
          sortableList.insertBefore(placeholder, afterElement);
        }
      }
    });

    // Helper function to find element after which to drop
    const getDragAfterElement = (container, y) => {
      const draggableElements = [...container.querySelectorAll(".draggable:not(.dragging)")];

      return draggableElements.reduce(
        (closest, child) => {
          const box = child.getBoundingClientRect();
          const offset = y - box.top - box.height / 2;

          if (offset < 0 && offset > closest.offset) {
            return { offset: offset, element: child };
          } else {
            return closest;
          }
        },
        { offset: Number.NEGATIVE_INFINITY }
      ).element;
    };

    // Handle drag end
    sortableList.addEventListener("dragend", (e) => {
      if (!draggedElement || !placeholder) return;

      // Save scroll position before re-rendering
      const contentElement = this.popup?.querySelector(".chamfr-column-config-content");
      if (contentElement) {
        this.savedScrollPosition = contentElement.scrollTop;
      }

      // Get all list items (excluding placeholder)
      const allItems = Array.from(sortableList.children).filter(
        (child) => !child.classList.contains("chamfr-column-config-placeholder")
      );

      const placeholderIndex = Array.from(sortableList.children).indexOf(placeholder);
      const oldIndex = draggedIndex;

      // Remove placeholder
      placeholder.remove();
      draggedElement.classList.remove("dragging");

      // Calculate new index based on placeholder position
      // Account for fixed first item (index 0) and fixed last item
      let newIndex = placeholderIndex;

      // Adjust for fixed first item
      if (newIndex === 0) {
        newIndex = 1; // Can't be before item-number
      }

      // Ensure we're not trying to place after price
      const maxIndex = this.selectedColumns.length - 1;
      if (newIndex >= maxIndex) {
        newIndex = maxIndex - 1; // Can't be after price
      }

      // Only reorder if position changed and within valid range
      if (newIndex !== oldIndex && newIndex >= 1 && newIndex < this.selectedColumns.length - 1) {
        const key = this.selectedColumns[oldIndex];

        // Don't move fixed columns
        if (key !== FIXED_FIRST && key !== FIXED_LAST) {
          // Remove from old position
          this.selectedColumns.splice(oldIndex, 1);

          // Adjust newIndex if we removed an item before it
          const adjustedNewIndex = oldIndex < newIndex ? newIndex - 1 : newIndex;

          // Insert at new position
          this.selectedColumns.splice(adjustedNewIndex, 0, key);

          // Re-render to update the UI
          this.render();
        }
      }

      draggedElement = null;
      draggedIndex = null;
      placeholder = null;
    });
  }

  /**
   * Handle reset action
   */
  handleReset() {
    // Save scroll position before re-rendering
    const contentElement = this.popup?.querySelector(".chamfr-column-config-content");
    if (contentElement) {
      this.savedScrollPosition = contentElement.scrollTop;
    }

    // Reset to default columns
    this.selectedColumns = [...this.defaultColumns];
    this.render();
  }

  /**
   * Handle save action
   */
  handleSave() {
    // Save to localStorage
    columnState.saveUserPreferences(this.currentCategory, this.selectedColumns);

    // Trigger update via global handler (set by middleware)
    if (window.chamfrColumnConfigUpdate) {
      window.chamfrColumnConfigUpdate(this.currentCategory, this.selectedColumns);
    }

    if (this.onSaveCallback) {
      this.onSaveCallback(this.currentCategory, this.selectedColumns);
    }

    this.close();
  }
}

// Export singleton instance
export const columnConfigPopup = new ColumnConfigPopup();
